import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, GitBranch, Clock, AlertTriangle, TrendingUp, Calendar } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";

interface StrategicPathProps {
  data: {
    current_phase?: string;
    next_decision_point?: string;
    transition_triggers?: string[];
    long_term_implications?: string;
  };
  alternativeOptions?: Array<{ label: string; when_this_option_fits?: string }>;
  reviewDate?: string;
}

export function StrategicPathSection({ data, alternativeOptions, reviewDate }: StrategicPathProps) {
  if (!data) return null;

  const hasTransitionTriggers = data.transition_triggers && data.transition_triggers.length > 0;

  const formattedReviewDate = reviewDate
    ? format(new Date(reviewDate), "d. MMMM yyyy", { locale: de })
    : null;

  return (
    <Card id="strategic-path" className="scroll-mt-24">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <GitBranch className="h-5 w-5 text-primary" />
          Strategischer Übergangspfad
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Current Decision */}
        {data.current_phase && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0 mt-0.5">
              <div className="w-3 h-3 rounded-full bg-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1">
                Aktueller Modus
              </p>
              <p className="text-sm font-medium break-words">{data.current_phase}</p>
            </div>
          </div>
        )}

        {/* Arrow connector */}
        {data.current_phase && data.next_decision_point && (
          <div className="flex items-center gap-3 pl-4">
            <div className="w-px h-6 bg-border ml-3.5" />
          </div>
        )}

        {/* Next Decision Point */}
        {data.next_decision_point && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0 mt-0.5">
              <Clock className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1">
                Nächster Entscheidungspunkt
              </p>
              {formattedReviewDate && (
                <div className="flex items-center gap-2 mb-1.5">
                  <Calendar className="h-3.5 w-3.5 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-700 dark:text-amber-400">
                    Review am {formattedReviewDate}
                  </span>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-500/30 text-amber-600">
                    Go/Pivot Gate
                  </Badge>
                </div>
              )}
              <p className="text-sm font-medium break-words">{data.next_decision_point}</p>
            </div>
          </div>
        )}

        {/* Transition Triggers */}
        {hasTransitionTriggers && (
          <div className="bg-muted/50 rounded-lg p-4 border border-border/50">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <h4 className="text-sm font-semibold">Wann wird ein Strategiewechsel nötig?</h4>
            </div>
            <ul className="space-y-2">
              {data.transition_triggers.map((trigger, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                  <span>{trigger}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Alternative Options */}
        {alternativeOptions && alternativeOptions.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <GitBranch className="h-4 w-4 text-muted-foreground" />
              Wann wäre eine andere Option besser?
            </h4>
            <div className="grid gap-2">
              {alternativeOptions.map((option, index) => (
                option.when_this_option_fits && (
                  <div key={index} className="bg-muted/30 rounded-lg p-3 border border-border/30">
                    <Badge variant="outline" className="mb-2 text-xs">
                      {option.label}
                    </Badge>
                    <p className="text-sm text-muted-foreground">
                      {option.when_this_option_fits}
                    </p>
                  </div>
                )
              ))}
            </div>
          </div>
        )}

        {/* Long-term Implications */}
        {data.long_term_implications && (
          <div className="flex items-start gap-3 pt-2 border-t border-border/50">
            <TrendingUp className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1">
                Langfristige Auswirkung (6-12 Monate)
              </p>
              <p className="text-sm">{data.long_term_implications}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
